import React, { Fragment, memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeStyles, styled } from "@mui/styles";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { Box, Typography as MuiTypography, Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { ConversationActions, NotificationSettingSelectors } from "redux-store";
import { NotificationsOffOutlined } from "@mui/icons-material";
import { getInteractor } from "services/local.service";
import { StorageUtil } from "utils";
import { AppAvatar } from "components";
import { getNSLang } from "utils/lang.utils";
import { convertLineChatTime } from "utils/date.utils";

const ThreadFlow = ({ parentMessageId, threadDetail = {} }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t: getLabel } = useTranslation(LangConstant.NS_HOME_CONVERSATION);
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const { messageThreadTotal, unreadMsgThreadTotal, accountReply, lastReplyTime } = threadDetail;

  const remoteUpdateList = useSelector(NotificationSettingSelectors.getUpdateThreadSetting);

  const [isTurnOffNotice, setIsTurnOffNotice] = useState(false);
  const [avatarIds, setAvatarIds] = useState([]);
  const [hasMoreReply, setHasMoreReply] = useState(false);

  const handleReplyMessage = () => {
    dispatch(
      ConversationActions.setSelectGroupId({
        threadingId: parentMessageId,
      }),
    );
  };

  const getAvatarList = async accountIds => {
    const accounts = await getInteractor(prefixKey).LocalAccountService.find({
      id: accountIds,
    });
    if (Array.isArray(accounts) && accounts.length > 0) {
      const avatarList = accountIds.map(accountId => {
        const account = accounts.find(item => item.id === accountId) || {};
        return account.avatar_id;
      });
      setAvatarIds(avatarList || []);
    } else {
      setAvatarIds([]);
    }
  };

  useEffect(() => {
    getInteractor()
      .LocalThreadSettingService.findOne({
        thread_id: parentMessageId,
        account_id: accountId,
      })
      .then(setting => {
        if (setting && setting.id) {
          setIsTurnOffNotice(setting.state === SystemConstant.STATE.inactive);
        } else {
          setIsTurnOffNotice(false);
        }
      });
  }, []);

  useEffect(() => {
    if (remoteUpdateList && remoteUpdateList.threadId === parentMessageId) {
      remoteUpdateList.updateList.forEach(setting => {
        if (setting.setting_sub_type === SystemConstant.SETTING_SUB_TYPE.NOTIFICATION_THREAD) {
          setIsTurnOffNotice(setting.state === SystemConstant.STATE.inactive);
        }
      });
    }
  }, [remoteUpdateList]);

  useEffect(() => {
    if (Array.isArray(accountReply) && accountReply.length > 0) {
      const displayAccountIds = accountReply.slice(0, 3);
      getAvatarList(displayAccountIds);
    }

    setHasMoreReply(accountReply && accountReply.length > 3);
  }, [accountReply]);

  const hasUnreadMessage = unreadMsgThreadTotal > 0;

  if (!parentMessageId) return <Fragment />;
  return (
    <ReplyComponent onClick={handleReplyMessage}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.25}>
        {avatarIds.map((avatarId, index) => (
          <AccountAvatar
            key={index}
            avatarId={avatarId}
            hasMoreAvatar={hasMoreReply && index === avatarIds.length - 1}
          />
        ))}
      </Stack>

      {hasUnreadMessage ? (
        <Typography className={classes.threadCountUnread}>
          {getLabel(LangConstant.FM_NEW_REPLY_COUNT, {
            number: unreadMsgThreadTotal,
          })}
        </Typography>
      ) : (
        <Typography className={classes.threadCount}>
          {getLabel(LangConstant.FM_REPLY_COUNT, {
            count: messageThreadTotal,
          })}
        </Typography>
      )}

      <Typography>
        {getNSLang(LangConstant.NS_HOME_CONVERSATION, "FM_LAST_REPLY_TIME", {
          time: convertLineChatTime(lastReplyTime),
        })}
      </Typography>

      {isTurnOffNotice && <NotificationOffIcon />}
    </ReplyComponent>
  );
};

export default memo(ThreadFlow);

const AccountAvatar = ({ avatarId, hasMoreAvatar = true }) => {
  return (
    <Box width="20px" height="20px">
      <Avatar avatarId={avatarId} size={20}></Avatar>
      {hasMoreAvatar && (
        <Stack alignItems="center" justifyContent="center">
          <ThirdAvatarLayout />
          <MuiTypography color="#FFFFFF" fontSize="12px" position="absolute">
            3+
          </MuiTypography>
        </Stack>
      )}
    </Box>
  );
};

const ReplyComponent = styled(Box)({
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  gap: 6,
});

const NotificationOffIcon = styled(NotificationsOffOutlined)(props => ({
  color: props.theme.palette.grey[600],
  width: 16,
  height: 16,
}));

const Typography = styled(MuiTypography)(props => ({
  width: "max-content",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: "16px",
  color: props.theme.palette.grey[600],
}));

const ThirdAvatarLayout = styled(Box)({
  width: 20,
  height: 20,
  borderRadius: "50%",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "#000000",
  opacity: "50%",
});

const Avatar = styled(AppAvatar)({
  position: "absolute",
});

const useStyles = makeStyles(theme => ({
  threadCount: {
    color: theme.palette.primary.main,
  },

  threadCountUnread: {
    color: theme.palette.secondary.main,
  },
}));
